// Pedestrian walking rules and animation.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { cam } from '../core/scene.js';
import { UP } from '../world/sagrada.js';
import { walkers } from './models.js';
import { S, wrapAngle } from '../game/ride.js';
import { state } from '../core/state.js';

export let WALK_CYCLE, WALK_SPEED, walk;

export function buildWalking() {
  walk = function walk(dt, time) {
    for (const w of walkers) {
      let s = w.s,
        k = 0;
      while (s > w.lens[k]) {
        s -= w.lens[k];
        k++;
      }
      const [ax, az] = w.path[k],
        [bx, bz] = w.path[(k + 1) % w.path.length],
        t = s / w.lens[k];
      const tx = (bx - ax) / w.lens[k],
        tz = (bz - az) / w.lens[k],
        mx = (ax + bx) / 2 - w.b.cx,
        mz = (az + bz) / 2 - w.b.cz,
        ml = Math.hypot(mx, mz);
      w.k = k;
      w.base = [ax + (bx - ax) * t, az + (bz - az) * t];
      w.out = [mx / ml, mz / ml];
      w.dir = [tx * Math.sign(w.v), tz * Math.sign(w.v)];
    }
    for (const w of walkers) {
      // far walkers are hidden and their bone hierarchy skips the per-frame world-matrix pass (the biggest CPU cost)
      const far = w.g.position.distanceToSquared(cam.position) > state.cullD2;
      w.g.visible = w.g.matrixWorldAutoUpdate = !far;
      if (w.dog) w.dog.g.visible = w.dog.g.matrixWorldAutoUpdate = !far;
      let want = Math.abs(w.v),
        lane = Math.sign(w.v) * 0.32 + (w.off || 0);
      if (Math.hypot(S.x - w.g.position.x, S.z - w.g.position.z) < 1.8) want = 0;
      for (const o of walkers) {
        if (o === w || o === w.mate || o.b !== w.b) continue;
        const dx = o.g.position.x - w.g.position.x,
          dz = o.g.position.z - w.g.position.z,
          ahead = dx * w.dir[0] + dz * w.dir[1];
        if (ahead <= 0 || ahead > 1.6) continue;
        const side = Math.abs(dx * w.dir[1] - dz * w.dir[0]);
        if (side > 0.55) continue;
        if (Math.sign(o.v) === Math.sign(w.v))
          want = Math.min(want, ahead < 0.7 ? 0 : Math.abs(o.vv));
        else lane = Math.sign(w.v) * 0.55 + (w.off || 0);
      }
      w.vv += (want - w.vv) * Math.min(1, 4 * dt);
      w.s = (((w.s + w.vv * Math.sign(w.v) * dt) % w.total) + w.total) % w.total;
      w.dist += w.vv * dt;
      w.lat += (lane - w.lat) * Math.min(1, 3 * dt);
      const x = w.base[0] + w.out[0] * w.lat,
        z = w.base[1] + w.out[1] * w.lat;
      const yaw = Math.atan2(w.dir[0], w.dir[1]);
      w.g.rotation.y += wrapAngle(yaw - w.g.rotation.y) * Math.min(1, 8 * dt);
      // gait cycle: one stride is ~1.3 m (0.33 units here)
      const ph = (w.dist / 0.33) * PI + w.ph,
        amp = Math.min(1, w.vv / 0.6),
        sw = Math.sin(ph) * 0.42 * amp;
      const gy = w.ground ? w.ground(z) : 0.3;
      w.g.position.set(x, gy + Math.abs(Math.cos(ph)) * 0.022 * amp, z);
      w.g.rotation.z = Math.sin(ph) * 0.025 * amp;
      if (w.model) {
        const m = w.model,
          wk = Math.min(1, w.vv / 0.25);
        // taller people take longer strides, so the cadence scales down with their size
        m.walkA.setEffectiveWeight(wk);
        m.idleA.setEffectiveWeight(1 - wk);
        m.walkA.timeScale = Math.max(0.2, w.vv / ((m.speed ?? WALK_SPEED) * w.g.scale.y));
        if (w.g.visible) m.mixer.update(dt);
        // heads turn: towards the scooter when it's close and in front, otherwise an occasional glance at the shops or the sea
        const bx = S.x - x,
          bz = S.z - z,
          bd = Math.hypot(bx, bz);
        let look =
          Math.sin(time * 0.23 + w.ph * 7) > 0.9 ? Math.sign(Math.sin(w.ph * 13)) * 0.55 : 0;
        if (bd < 7) {
          const a = wrapAngle(Math.atan2(bx, bz) - w.g.rotation.y);
          if (Math.abs(a) < 2) look = THREE.MathUtils.clamp(a, -1, 1);
        }
        w.look += (look - w.look) * Math.min(1, 3 * dt);
        if (w.g.visible && m.head && Math.abs(w.look) > 0.01) {
          const hd = m.head,
            pq = hd.parent.getWorldQuaternion(walkQ1);
          hd.quaternion.premultiply(
            walkQ2.copy(pq).invert().multiply(walkQ3.setFromAxisAngle(UP, w.look)).multiply(pq),
          );
        }
        // the body rises over each planted foot: twice per cycle
        w.g.rotation.z = 0;
        w.g.position.y =
          gy + 0.012 * wk * Math.cos((4 * PI * m.walkA.time) / (m.cycle ?? WALK_CYCLE));
      } else {
        const [h0, h1, s0, s1] = w.legs;
        h0.rotation.x = -sw;
        h1.rotation.x = sw;
        h0.userData.j.rotation.x = (0.08 + Math.max(0, Math.sin(ph + 1.3)) * 0.75) * amp;
        h1.userData.j.rotation.x = (0.08 + Math.max(0, Math.sin(ph + 1.3 + PI)) * 0.75) * amp;
        s0.rotation.x = sw * 0.75;
        s1.rotation.x = -sw * 0.75;
        s0.userData.j.rotation.x = -0.2 - Math.max(0, -Math.sin(ph)) * 0.35 * amp;
        s1.userData.j.rotation.x = -0.2 - Math.max(0, Math.sin(ph)) * 0.35 * amp;
      }
      if (w.dog) {
        const h = w.g.rotation.y,
          fx = Math.sin(h),
          fz = Math.cos(h);
        w.dog.g.position.set(x + fz * 0.45 + fx * 0.25, 0.36, z - fx * 0.45 + fz * 0.25);
        w.dog.g.rotation.y = h;
        const tr = Math.sin(ph * 2) * 0.6 * amp;
        w.dog.legs.forEach((l, k) => (l.rotation.x = k % 3 ? tr : -tr));
      }
    }
  };
  // Pedestrians keep to their right on the sidewalk. They slow behind slower people, step aside for oncoming
  // ones, and stop for the scooter. The gait is driven by distance walked, so feet don't slide.
  // walking speed (world units / s) at which the Walk clip plays at its natural rate; tuned so feet don't slide
  WALK_CYCLE = 1.1;
  WALK_SPEED = 0.64;
  state.cullD2 = 48 * 48;
  const walkQ1 = new THREE.Quaternion(),
    walkQ2 = new THREE.Quaternion(),
    walkQ3 = new THREE.Quaternion();
}
